/**
 * Custom next/image loader that uses Cloudflare Image Transformations
 * against the R2/custom assets domain (or falls back to the given src).
 *
 * Requires NEXT_PUBLIC_ASSETS_URL (e.g. https://assets.theshow.com.au)
 * and Cloudflare Image Transformations enabled on that zone.
 *
 * When the account hits the free unique-transformation cap (9422), append
 * onerror=redirect so Cloudflare serves the original R2 file instead of a
 * broken image. Set NEXT_PUBLIC_CF_IMAGE_TRANSFORMS=false to bypass CF
 * entirely and route via Statamic Glide (/img/assets/...?w=).
 *
 * docs: https://developers.cloudflare.com/images/transform-images/transform-via-url/
 */
export default function cloudflareImageLoader({ src, width, quality }) {
  const assetsBase = process.env.NEXT_PUBLIC_ASSETS_URL?.replace(/\/$/, "");
  const cfTransformsEnabled =
    process.env.NEXT_PUBLIC_CF_IMAGE_TRANSFORMS !== "false";

  // Local public files (e.g. /assets/logos/..., /brand/...) must stay same-origin.
  // Only the CMS proxy path `/img/assets/...` maps onto the R2 CDN.
  if (src.startsWith("/") && !src.startsWith("/img/assets")) {
    return src;
  }

  // Same-origin /img/assets paths use Statamic Glide (see route.js) — e.g. sources
  // over Cloudflare's 10,000px / 100MP limits, or when CF transforms are disabled.
  if (src.startsWith("/img/assets")) {
    return glideProxyUrl(src, width, quality);
  }

  let absolute = src;

  if (!assetsBase || !absolute.startsWith("http")) {
    return src;
  }

  // Don't run CF transforms for non-CDN hosts
  if (!absolute.startsWith(assetsBase)) {
    return src;
  }

  // SVGs should be served as-is — CF image transforms are for raster assets
  if (/\.svg(\?|#|$)/i.test(absolute)) {
    return absolute;
  }

  // Already a CF transform URL — leave as-is (avoid double-wrapping)
  if (absolute.includes("/cdn-cgi/image/")) {
    return absolute;
  }

  const q = quality || 75;
  const path = absolute.replace(/^https?:\/\/[^/]+/, "");

  if (!cfTransformsEnabled) {
    return glideProxyUrl(`/img/assets${path}`, width, q);
  }

  // onerror=redirect: when CF can't transform (9422 quota, too-large source,
  // etc.), serve the original from the same assets domain instead of erroring.
  return `${assetsBase}/cdn-cgi/image/width=${width},quality=${q},format=auto,onerror=redirect${path}`;
}

/** Same-origin Statamic Glide proxy (see src/app/img/assets/[...path]/route.js). */
function glideProxyUrl(relativeSrc, width, quality) {
  const join = relativeSrc.includes("?") ? "&" : "?";
  return `${relativeSrc}${join}w=${width}&q=${quality || 75}&fit=max`;
}
